import { useCallback, useReducer } from 'react'
import { useMutation } from '@/app/common/graphql/hooks'
import { trackEvent } from '@/helpers/tracking'
import useCompany from '@/helpers/useCompany'
import {
  AppraiseItemDocument,
  AppraiseItemMutation,
  AppraiseItemMutationVariables,
  EAppraisalQuestionInputType,
} from '@/types/gql/graphql'

export const MAX_DESCRIPTION_LENGTH = 200
export const MIN_WEIGHT_GRAMS = 1
export const MAX_WEIGHT_GRAMS = 2000

export type AppraisalPhase =
  | 'landing'
  | 'loadingQuestions'
  | 'loadingEstimate'
  | 'questions'
  | 'result'
  | 'rejected'
  | 'handoff'
  | 'request'
  | 'error'

export type AppraisalStepResult = AppraiseItemMutation['appraiseItem']
export type AppraisalQuestion = Extract<
  AppraisalStepResult,
  { __typename: 'AppraisalQuestionsStep' }
>['questions'][number]
export type AppraisalValuation = Extract<
  AppraisalStepResult,
  { __typename: 'AppraisalValuationResult' }
>
export type AppraisalHandoff = Extract<
  AppraisalStepResult,
  { __typename: 'AppraisalHandoffResult' }
>
export type AppraisalRequest = Extract<
  AppraisalStepResult,
  { __typename: 'AppraisalRequestResult' }
>

export interface AnsweredQuestion {
  questionTitle: string
  valueLabel: string
}

interface AppraisalState {
  phase: AppraisalPhase
  text: string
  productName: string
  context: string | null
  questions: AppraisalQuestion[]
  /** False while further questions depend on the current answers. */
  isFinalStep: boolean
  /** key → CHOICE: option index as string; NUMBER: entered value. */
  answers: Record<string, string>
  submittedAnswers: AnsweredQuestion[]
  valuation: AppraisalValuation | null
  rejectionMessage: string
  rejectionReason: string
  handoff: AppraisalHandoff | null
  /** No online estimate — the customer names their ask and sends a request. */
  request: AppraisalRequest | null
  lastVariables: Omit<AppraiseItemMutationVariables, 'companyId'> | null
  /** Number of question rounds shown so far. */
  rounds: number
}

const initialState: AppraisalState = {
  phase: 'landing',
  text: '',
  productName: '',
  context: null,
  questions: [],
  isFinalStep: true,
  answers: {},
  submittedAnswers: [],
  valuation: null,
  rejectionMessage: '',
  rejectionReason: '',
  handoff: null,
  request: null,
  lastVariables: null,
  rounds: 0,
}

type AppraisalAction =
  | {
      type: 'REQUEST_STARTED'
      variables: Omit<AppraiseItemMutationVariables, 'companyId'>
      answeredNow: AnsweredQuestion[]
    }
  | { type: 'RESPONSE_RECEIVED'; result: AppraisalStepResult }
  | { type: 'SET_ANSWER'; key: string; value: string | null }
  | { type: 'REQUEST_FAILED' }
  | { type: 'RESET' }

function reducer(
  state: AppraisalState,
  action: AppraisalAction,
): AppraisalState {
  switch (action.type) {
    case 'REQUEST_STARTED':
      return {
        ...state,
        phase: action.variables.context
          ? 'loadingEstimate'
          : 'loadingQuestions',
        text: action.variables.text,
        lastVariables: action.variables,
        submittedAnswers: action.variables.context
          ? [
              // adjusted answers replace their earlier summary entry
              ...state.submittedAnswers.filter(
                (answered) =>
                  !action.answeredNow.some(
                    (now) => now.questionTitle === answered.questionTitle,
                  ),
              ),
              ...action.answeredNow,
            ]
          : [],
      }
    case 'RESPONSE_RECEIVED': {
      switch (action.result.__typename) {
        case 'AppraisalQuestionsStep':
          return {
            ...state,
            phase: 'questions',
            productName: action.result.productName || state.productName,
            context: action.result.context,
            questions: action.result.questions,
            isFinalStep: action.result.isFinalStep,
            answers: {},
            rounds: state.rounds + 1,
          }
        case 'AppraisalValuationResult': {
          // a valuation may carry editable, prefilled inputs (e.g. gold
          // weight/fineness) — keep them in the regular questions/answers
          // state so the result screen can resubmit adjusted values
          const adjustQuestions = action.result.adjustQuestions ?? []
          return {
            ...state,
            phase: 'result',
            productName: action.result.productName || state.productName,
            valuation: action.result,
            context: action.result.adjustContext ?? state.context,
            questions: adjustQuestions,
            answers: Object.fromEntries(
              adjustQuestions
                .filter((question) => question.prefill != null)
                .map((question) => [question.key, question.prefill as string]),
            ),
          }
        }
        case 'AppraisalRejectionResult':
          return {
            ...state,
            phase: 'rejected',
            rejectionMessage: action.result.message,
            rejectionReason: action.result.reason,
            questions: [],
            answers: {},
          }
        case 'AppraisalHandoffResult':
          return {
            ...state,
            phase: 'handoff',
            handoff: action.result,
            questions: [],
            answers: {},
          }
        case 'AppraisalRequestResult':
          return {
            ...state,
            phase: 'request',
            productName: action.result.productName || state.productName,
            request: action.result,
            questions: [],
            answers: {},
          }
        default:
          return { ...state, phase: 'error' }
      }
    }
    case 'SET_ANSWER': {
      const answers = { ...state.answers }
      if (action.value === null) {
        delete answers[action.key]
      } else {
        answers[action.key] = action.value
      }
      return { ...state, answers }
    }
    case 'REQUEST_FAILED':
      return { ...state, phase: 'error' }
    case 'RESET':
      return initialState
    default:
      return state
  }
}

/** Accepted range of a NUMBER question (the gold weight bounds by default). */
export function numberRange(question?: {
  min?: number | null
  max?: number | null
}): { min: number; max: number } {
  return {
    min: question?.min ?? MIN_WEIGHT_GRAMS,
    max: question?.max ?? MAX_WEIGHT_GRAMS,
  }
}

export function isNumberAnswerValid(
  value: string | undefined,
  question?: { min?: number | null; max?: number | null },
): boolean {
  if (!value) {
    return false
  }

  const parsed = Number(value.replace(',', '.'))
  const { min, max } = numberRange(question)

  return Number.isFinite(parsed) && parsed >= min && parsed <= max
}

export function useAppraisalFlow() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const company = useCompany()

  const [appraiseItem] = useMutation<
    AppraiseItemMutation,
    AppraiseItemMutationVariables
  >(AppraiseItemDocument, {
    // errors are rendered by the flow's own error view, not the snackbar
    disableDefaultErrorHandling: true,
  })

  const runRequest = useCallback(
    async (
      variables: Omit<AppraiseItemMutationVariables, 'companyId'>,
      answeredNow: AnsweredQuestion[],
    ) => {
      if (!company) {
        dispatch({ type: 'REQUEST_FAILED' })
        return
      }

      dispatch({ type: 'REQUEST_STARTED', variables, answeredNow })

      try {
        const response = await appraiseItem({
          variables: { ...variables, companyId: company._id },
        })

        if (response.error || !response.data) {
          dispatch({ type: 'REQUEST_FAILED' })
          return
        }

        dispatch({
          type: 'RESPONSE_RECEIVED',
          result: response.data.appraiseItem,
        })
      } catch {
        dispatch({ type: 'REQUEST_FAILED' })
      }
    },
    [appraiseItem, company],
  )

  const submitDescription = useCallback(
    (text: string) => {
      const trimmed = text.trim().slice(0, MAX_DESCRIPTION_LENGTH)

      if (!trimmed) {
        return
      }

      trackEvent('appraisal_started', { text_length: trimmed.length })
      runRequest({ text: trimmed, context: null, answers: [] }, [])
    },
    [runRequest],
  )

  const submitAnswers = useCallback(() => {
    const answeredNow: AnsweredQuestion[] = []
    const answers = state.questions
      // an optional question left blank is submitted empty so the server
      // knows it was asked (and does not ask again)
      .filter(
        (question) =>
          state.answers[question.key] !== undefined || question.optional,
      )
      .map((question) => {
        const value = state.answers[question.key] ?? ''
        const valueLabel =
          question.inputType === EAppraisalQuestionInputType.Choice
            ? question.options?.[parseInt(value, 10)] ?? value
            : `${value}${question.unit ? ` ${question.unit}` : ''}`
        if (value !== '') {
          answeredNow.push({ questionTitle: question.title, valueLabel })
        }
        return { key: question.key, value }
      })

    runRequest(
      { text: state.text, context: state.context, answers },
      answeredNow,
    )
  }, [runRequest, state])

  const setAnswer = useCallback((key: string, value: string | null) => {
    dispatch({ type: 'SET_ANSWER', key, value })
  }, [])

  const retry = useCallback(() => {
    if (state.lastVariables) {
      runRequest(state.lastVariables, [])
    }
  }, [runRequest, state.lastVariables])

  const reset = useCallback(() => dispatch({ type: 'RESET' }), [])

  const canSubmitAnswers = state.questions.every((question) => {
    const value = state.answers[question.key]

    // unanswered or explicitly skipped ("I don't know" → '')
    if (question.optional && (value === undefined || value === '')) {
      return true
    }

    if (question.inputType === EAppraisalQuestionInputType.Number) {
      return isNumberAnswerValid(value, question)
    }

    return value !== undefined
  })

  return {
    ...state,
    canSubmitAnswers,
    submitDescription,
    submitAnswers,
    setAnswer,
    retry,
    reset,
  }
}
